import type { DateString, MaybePromise, Method } from '~/types/CommonTypes';

import { computed, reactive } from 'vue';
import { defineStore } from 'pinia';

export const useEventStore = defineStore(
  'MY_SCAN_SOURCE_EVENT_STORE',
  () => {
    const events = reactive<Map<string, Array<Method<MaybePromise<void>, any>>>>(
      new Map(),
    );
    const logs = reactive<Map<string, TEventLog>>(new Map());
    const loadingMassiveData = ref(false);

    function toggleLoadingMassiveData() {
      loadingMassiveData.value = !loadingMassiveData.value;
    }

    function create(eventName: string | string[]) {
      if (Array.isArray(eventName)) return eventName.forEach((name) => create(name));

      if (!events.has(eventName)) {
        events.set(eventName, []);

        return registerLog({
          date: new Date().toISOString() as DateString,
          action: 'createEvent',
          eventName,
          success: true,
          details: 'Event created',
        });
      }

      return registerLog({
        date: new Date().toISOString() as DateString,
        action: 'createEvent',
        eventName,
        success: false,
        details: 'Event already exists',
      });
    }

    function register(params: RegisterEventParams) {
      try {
        const { eventName, callback, callbacks } = params;

        if (!eventName) {
          throw new Error("Cannot register event without it's name", {
            cause: { reason: 'NoEventName' },
          });
        }

        if (!callback && !callbacks) {
          if (!events.has(eventName)) return create(eventName);

          throw new Error('Cannot register event without a callback', {
            cause: { reason: 'NoCallback', eventName },
          });
        }

        if (!events.has(eventName)) create(eventName);

        if (callbacks && callbacks.length > 0)
          return registerMultipleCallbacks(eventName, callbacks);

        if (callback) {
          events.get(eventName)?.push(callback);

          return registerLog({
            date: new Date().toISOString() as DateString,
            action: 'registerEvent',
            eventName,
            success: true,
            details: 'Event callback registered',
          });
        }
      } catch (err) {
        if (err instanceof Error) {
          const { cause } = err;

          registerLog({
            date: new Date().toISOString() as DateString,
            action: 'registerEvent',
            success: false,
            details: (() => {
              switch (cause) {
                case 'NoEventName':
                  return "Cannot register event without it's name";
                case 'NoCallback':
                  return 'Cannot register event without a callback';
                default:
                  return 'Unknown reason';
              }
            })(),
          });
        }

        throw err;
      }
    }

    async function process(eventName: string, ...params: [any?]) {
      try {
        if (!events.has(eventName)) {
          throw new Error(`No callbacks registered for event: ${eventName}`, {
            cause: {
              reason: 'NoEventRegistered',
              eventName,
            },
          });
        }

        const callbacks = events.get(eventName);

        if (!callbacks || callbacks.length === 0) {
          throw new Error(`No callbacks registered for event: ${eventName}`, {
            cause: {
              reason: 'NoEventCallbacksRegistered',
              eventName,
            },
          });
        }

        return await Promise.allSettled(
          callbacks.map((callback) => callback(...params)),
        ).then((results) => {
          registerLog({
            date: new Date().toISOString() as DateString,
            action: 'processEvent',
            eventName,
            success: results.every((r) => r.status === 'fulfilled'),
            details: results.every((r) => r.status === 'fulfilled')
              ? `Event processed successfully`
              : `Event processed with errors`,
            results: results.map((r) => {
              if (r.status === 'fulfilled') {
                return r.value;
              }

              return r.reason.message;
            }),
          });

          return results.map((r) => {
            if (r.status === 'fulfilled') {
              return r.value;
            }

            return r.reason.message;
          });
        });
      } catch (err) {
        if (err instanceof Error) {
          const { cause } = err;

          registerLog({
            date: new Date().toISOString() as DateString,
            action: 'processEvent',
            eventName,
            success: false,
            details: (() => {
              switch (cause) {
                case 'NoEventRegistered':
                  return `No event created for: ${eventName}`;
                case 'NoEventCallbacksRegistered':
                  return `No callbacks registered for event: ${eventName}`;
                default:
                  return 'Unknown reason';
              }
            })(),
          });
        }
        throw err;
      }
    }

    function registerMultipleCallbacks(
      eventName: string,
      callbacks: Array<Method<MaybePromise<void>, any>>,
    ) {
      callbacks.forEach((callback) => {
        register({ eventName, callback });
      });
    }

    function registerLog(params: TEventLog) {
      logs.set(params.eventName ?? 'NoEvent', params);
    }

    return {
      logs,
      loadingMassiveData: computed(() => loadingMassiveData.value),
      events: computed(() => Array.from(events.keys())),
      toggleLoadingMassiveData,
      create,
      register,
      process,
    };
  },
  { persist: true },
);

export type TEventCallback = Method<MaybePromise<void>, any>;
export type RegisterEventParams = {
  eventName: string;
  callback?: TEventCallback;
  callbacks?: TEventCallback[];
};

export type TEventLog = {
  date: DateString;
  action: string;
  success: boolean;
  details: string;
  eventName?: string;
  results?: any;
};
